'use client';

import React, {FC} from 'react';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import {usePathname} from 'next/navigation';

import {renderUtils} from '@zoefin/design-system';
import {Button} from '@zoefin/design-system/v2';

import {useFormContext} from '../../form';

import StepIntroLogo from './components/StepIntroLogo';
import {getAffiliateLogo, getAffiliatePathFlags} from './utils';

import styles from './styles.module.scss';

const STEPS = [
  'Fill out the form',
  'Get advisor matches',
  'Book a free consultation',
];

const StepIntro: FC = () => {
  const {additionalProps, next} = useFormContext();
  const pathname = usePathname();

  renderUtils.useMount(() => {
    // Skip this step if `skip` includes `intro`
    if (additionalProps.skip.includes('intro')) next();
  });

  // Do not render this step if `skip` includes `intro`
  if (additionalProps.skip.includes('intro')) return null;

  const pathFlags = getAffiliatePathFlags(pathname);
  const {isAffiliate, isMyBankTracker, isCNN} = pathFlags;
  const affiliateLogo = getAffiliateLogo(pathFlags);

  return (
    <div className={styles.stepIntro}>
      <div className={styles.stepIntro__topContainer}>
        <div className={styles.stepIntro__logo}>
          <StepIntroLogo
            affiliateLogo={affiliateLogo}
            isMyBankTracker={isMyBankTracker}
            isCNN={isCNN}
          />
        </div>
        <h1 className={styles.stepIntro__title}>
          {isAffiliate
            ? 'Find a Trusted Financial Advisor'
            : 'An Easy Way To Find A Financial Advisor'}
        </h1>
        <h3 className={styles.stepIntro__description}>
          {isAffiliate ? (
            <>
              Fill out this 3 minute form to connect with
              <b>vetted fiduciary advisors</b>.
            </>
          ) : (
            <>
              Fill out this 2 minute form to connect with
              <b>your top advisor matches</b>.
            </>
          )}
        </h3>

        <ul className={styles.stepper}>
          {STEPS.map((step, idx) => (
            <li
              key={step}
              className={cn(styles.stepper__step, {
                [styles.stepper__step__modActive]: idx === 0,
              })}>
              <div
                className={cn(styles.stepper__step_circle, {
                  [styles.stepper__step_circle__modActive]: idx === 0,
                })}
              />
              {step}
            </li>
          ))}
        </ul>
      </div>

      <Button
        as="button"
        className={styles.stepIntro__cta}
        onClick={next}>{`Let's start`}</Button>

      <div className={styles.stepIntro__footer}>
        <div className={styles.stepIntro__footer_login}>
          Already completed this form?
          <Link href="/login">Log In</Link>
        </div>
        <div className={styles.stepIntro__footer_security}>
          <div>
            <Image
              src="/static/svg/flat/icon_lock_darkblue.svg"
              alt="lock icon"
              width={12}
              height={14}
            />
          </div>
          Your information is secure.
        </div>
      </div>
    </div>
  );
};

export default StepIntro;
